import { api } from "./api";

export type PensionContractKind = "GESETZLICH" | "BAV" | "RIESTER" | "RUERUP" | "PRIVAT";
export type PensionTargetMode = "EXPENSE_25X" | "MANUAL";

export interface PensionContract {
  id: string;
  name: string;
  kind: PensionContractKind;
  provider: string | null;
  contractNumber: string | null;
  startDate: string;
  monthlyContributionCents: number | null;
  expectedRetirementAge: number | null;
  projectedMonthlyPensionCents: number | null;
  notes: string | null;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface PensionSummary {
  contractCount: number;
  totalMonthlyContributionCents: number;
  totalProjectedMonthlyPensionCents: number;
}

export interface PensionSettings {
  targetMode: PensionTargetMode;
  manualTargetCents: number | null;
  annualExpenseCentsOverride: number | null;
  assumedRealReturnBp: number;
  assumedInflationBp: number;
  targetRetirementAge: number;
  birthYear: number | null;
  monthlyContributionCentsOverride: number | null;
}

export interface FireProjectionPoint {
  year: number;
  age: number | null;
  networthCents: number;
}

export interface FireProjection {
  currentNetworthCents: number;
  monthlyContributionCents: number;
  annualExpenseCents: number;
  targetCents: number;
  assumedRealReturnBp: number;
  currentAge: number | null;
  targetRetirementAge: number;
  yearsToTarget: number | null;
  projectedTargetAge: number | null;
  isAlreadyAtTarget: boolean;
  projection: FireProjectionPoint[];
  warnings: string[];
}

export interface CreatePensionContractInput {
  name: string;
  kind: PensionContractKind;
  provider?: string;
  contractNumber?: string;
  startDate: string;
  monthlyContributionCents?: number;
  expectedRetirementAge?: number;
  projectedMonthlyPensionCents?: number;
  notes?: string;
}

export type UpdatePensionContractInput = Partial<{
  name: string;
  kind: PensionContractKind;
  provider: string | null;
  contractNumber: string | null;
  startDate: string;
  monthlyContributionCents: number | null;
  expectedRetirementAge: number | null;
  projectedMonthlyPensionCents: number | null;
  notes: string | null;
  isActive: boolean;
}>;

export type UpdatePensionSettingsInput = Partial<{
  targetMode: PensionTargetMode;
  manualTargetCents: number | null;
  annualExpenseCentsOverride: number | null;
  assumedRealReturnBp: number;
  assumedInflationBp: number;
  targetRetirementAge: number;
  birthYear: number | null;
  monthlyContributionCentsOverride: number | null;
}>;

export const pensionApi = {
  listContracts: (active?: boolean) =>
    api<{ contracts: PensionContract[] }>(
      `/pension/contracts${active === undefined ? "" : `?active=${active}`}`,
    ),
  summary: () => api<PensionSummary>("/pension/contracts/summary"),
  getContract: (id: string) => api<PensionContract>(`/pension/contracts/${id}`),
  createContract: (input: CreatePensionContractInput) =>
    api<PensionContract>("/pension/contracts", { method: "POST", body: input }),
  updateContract: (id: string, input: UpdatePensionContractInput) =>
    api<PensionContract>(`/pension/contracts/${id}`, { method: "PATCH", body: input }),
  softDeleteContract: (id: string) => api<void>(`/pension/contracts/${id}`, { method: "DELETE" }),
  hardDeleteContract: (id: string) =>
    api<void>(`/pension/contracts/${id}/hard`, { method: "DELETE" }),
  getSettings: () => api<PensionSettings>("/pension/settings"),
  updateSettings: (input: UpdatePensionSettingsInput) =>
    api<PensionSettings>("/pension/settings", { method: "PATCH", body: input }),
  fireProjection: () => api<FireProjection>("/pension/fire-projection"),
};
